import {useState, useEffect} from "react";
import {useNavigate, useSearchParams} from "react-router";
import {Capacitor} from "@capacitor/core";
import {getCurrentUser} from "../services/usersService";
import {flushPendingQrScan} from "../utils/scanEvents";
import {useAuth} from "../contexts/AuthContext";
import {PATHS} from "../routing";

/** Сообщение над формой входа, когда сессию не удалось восстановить при заходе на сайт. */
const SESSION_EXPIRED_NOTICE = "Сессия истекла — пожалуйста, войдите снова.";

export function useLandingHelpers() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const {sessionExpired, clearSessionExpired} = useAuth();
    const returnTo = (searchParams.get("return") ?? "").trim();
    const authParam = (searchParams.get("auth") ?? "").trim();
    // Токен сброса из ссылки письма (/reset-password?token=…). Поддерживается и
    // легаси-имя ?reset= — на случай старых писем с таким адресом.
    const resetToken = (searchParams.get("token") ?? searchParams.get("reset") ?? "").trim();

    // ?auth=register ведёт на полноценную страницу регистрации: в модалке формы
    // регистрации нет, она умеет только логин и сброс пароля.
    const [showAuth, setShowAuth] = useState(() => authParam === "login" || resetToken.length > 0);
    const [authInitialMode, setAuthInitialMode] = useState<"login" | "register" | "resetToken">(() =>
        authParam === "login" ? "login" : resetToken ? "resetToken" : "login",
    );
    const [authNotice, setAuthNotice] = useState<string | null>(null);

    // Заход на сайт с протухшими токенами: сразу открываем вход с пояснением.
    // Флаг в контексте сбрасываем, чтобы модалка не открывалась повторно.
    useEffect(() => {
        if (!sessionExpired) return;
        setAuthInitialMode("login");
        setAuthNotice(SESSION_EXPIRED_NOTICE);
        setShowAuth(true);
        clearSessionExpired();
    }, [clearSessionExpired, sessionExpired]);

    useEffect(() => {
        if (authParam !== "register") return;
        const q = returnTo.startsWith("/") ? `?return=${encodeURIComponent(returnTo)}` : "";
        navigate(`${PATHS.register}${q}`, {replace: true});
    }, [authParam, returnTo, navigate]);

    // Завершили сброс пароля и пришли «Войти» (/?auth=login): переключаем модалку
    // на обычный вход. Без этого она осталась бы в режиме resetToken — токен из
    // адреса к этому моменту уже убран переходом, а режим живёт в стейте.
    useEffect(() => {
        if (authParam !== "login") return;
        setAuthInitialMode("login");
        setAuthNotice(null);
        setShowAuth(true);
    }, [authParam]);

    const openRegister = () => {
        setShowAuth(false);
        setAuthNotice(null);
        navigate(PATHS.register);
    };

    const openLogin = () => {
        setAuthInitialMode("login");
        setAuthNotice(null);
        setShowAuth(true);
    };

    const closeAuth = () => {
        setShowAuth(false);
        setAuthNotice(null);
        if (resetToken) {
            // Закрыли окно восстановления — убираем токен из адреса, чтобы F5
            // не открывал форму с уже израсходованным токеном.
            navigate(PATHS.landing, {replace: true});
        }
    };

    const handleAuthSuccess = async () => {
        closeAuth();
        const res = await getCurrentUser();
        if (!res.data) return;
        await flushPendingQrScan();
        if (returnTo.startsWith("/")) {
            navigate(returnTo, {replace: true});
            return;
        }
        navigate(PATHS.app, {replace: true});
    };

    // Модалка входа смонтирована как точка входа Capacitor-обёртки (appStartPath
    // = `/?auth=login`), а не открыта человеком, который уже листал лендинг —
    // ровно тот же признак, которым выше сеется начальный showAuth. Не хранится
    // в стейте намеренно: значение должно оставаться производным от текущего
    // URL, а не «застывать» на моменте первого рендера.
    // Только в нативной обёртке: в браузере тот же `/?auth=login` открывают
    // после сброса пароля («Войти» в ResetToken) и по ссылкам с регистрации —
    // там вход должен остаться карточкой по центру, а не страницей на весь
    // экран (#1212).
    const entryMode = authParam === "login" && Capacitor.isNativePlatform();

    return {
        openRegister,
        openLogin,
        showAuth,
        setShowAuth,
        closeAuth,
        authInitialMode,
        authNotice,
        resetToken,
        entryMode,
        handleAuthSuccess
    };
}
